import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../../config/axios';
import StripeAmountSelector from './StripeAmountSelector';
import StripePaymentForm from './StripePaymentForm';

const QUOTE_REFRESH_INTERVAL = 60 * 1000; // 1 minute

const StripeOnRamp = ({ walletAddress, theme }) => {
  const [usdAmount, setUsdAmount] = useState('50');
  const [kaxAmount, setKaxAmount] = useState('');
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(true);
  const [quoteError, setQuoteError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentSucceeded, setPaymentSucceeded] = useState(false);

  const hasWalletAddress = walletAddress && walletAddress !== '';
  const presetAmounts = ['30', '50', '100', '200'];

  const fetchQuote = useCallback(async () => {
    try {
      setQuoteError(null);
      const response = await axiosInstance.get('/api/stripe/quote');
      if (response.data.success && response.data.data.kaxPrice > 0) {
        setQuote(response.data.data);
      } else {
        setQuoteError('Live KAX price unavailable right now.');
      }
    } catch (err) {
      console.error('Error fetching Stripe quote:', err);
      setQuoteError('Unable to load live pricing.');
    } finally {
      setQuoteLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuote();
    const interval = setInterval(fetchQuote, QUOTE_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchQuote]);

  // USD is the amount actually charged - KAX is always derived from it (and the fee) whenever
  // either the USD amount or the live price/fee quote changes.
  useEffect(() => {
    if (quote?.kaxPrice > 0 && usdAmount) {
      const usd = parseFloat(usdAmount) || 0;
      const fee = usd * quote.feePercent + quote.feeFixed;
      const net = Math.max(usd - fee, 0);
      setKaxAmount((net / quote.kaxPrice).toFixed(4));
    }
  }, [usdAmount, quote]);

  const handleUsdChange = (val) => {
    setUsdAmount(val);
  };

  // Editing the KAX field back-solves for the USD amount that must be charged so that, after
  // the Stripe fee is deducted, the user nets this much KAX.
  const handleKaxChange = (val) => {
    setKaxAmount(val);
    if (quote?.kaxPrice > 0 && val) {
      const kax = parseFloat(val) || 0;
      const netUsd = kax * quote.kaxPrice;
      const usd = (netUsd + quote.feeFixed) / (1 - quote.feePercent);
      setUsdAmount(usd.toFixed(2));
    }
  };

  const handlePresetClick = (val) => {
    setUsdAmount(val);
  };

  const handleProceed = async () => {
    if (parseFloat(usdAmount) < 10) return;

    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.post('/api/stripe/create-payment-intent', {
        amount: usdAmount,
        walletAddress,
      });

      if (response.data.success && response.data.data.clientSecret) {
        setClientSecret(response.data.data.clientSecret);
      } else {
        setError('Failed to create Stripe payment.');
      }
    } catch (err) {
      console.error('Error creating Stripe payment intent:', err);
      const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to initialize payment. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setClientSecret(null);
    setError(null);
    setPaymentSucceeded(false);
  };

  return (
    <div id="tour-stripe-widget" className="card account-card overflow-hidden border-0 shadow-sm mt-4">
      <style dangerouslySetInnerHTML={{ __html: `
        .btn-custom-theme-active {
          background-color: var(--accent-color) !important;
          border-color: var(--accent-color) !important;
          color: #ffffff !important;
        }
        .btn-custom-theme-active:hover,
        .btn-custom-theme-active:focus,
        .btn-custom-theme-active:active {
          background-color: var(--accent-color-active) !important;
          border-color: var(--accent-color-active) !important;
          color: #ffffff !important;
        }
        .btn-custom-theme-outline {
          background-color: transparent !important;
          border: 1px solid var(--accent-color) !important;
          color: var(--accent-color) !important;
        }
        .btn-custom-theme-outline:hover,
        .btn-custom-theme-outline:focus,
        .btn-custom-theme-outline:active {
          background-color: var(--accent-color) !important;
          border-color: var(--accent-color) !important;
          color: #ffffff !important;
        }
        .custom-amount-input:focus {
          border-color: var(--accent-color) !important;
          box-shadow: 0 0 0 0.25rem rgba(161, 204, 60, 0.25) !important;
        }
      `}} />
      <div className="p-4">
        <div className="position-relative mb-4 d-flex align-items-center justify-content-center" style={{ minHeight: '32px' }}>
          {clientSecret && (
            <button
              type="button"
              onClick={handleReset}
              className="btn btn-link p-0 text-muted d-flex align-items-center gap-1 text-decoration-none border-0 bg-transparent hover:text-dark position-absolute start-0"
              style={{ cursor: 'pointer', outline: 'none' }}
              title="Go Back"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
              </svg>
              <span className="font-semibold text-sm">Back</span>
            </button>
          )}
          <h5 className="font-semibold text-lg mb-0 text-center">
            Buy KAX with Stripe
          </h5>
        </div>

        {!hasWalletAddress ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading wallet address...</span>
            </div>
            <p className="mt-2 text-muted text-sm">Loading your wallet address...</p>
          </div>
        ) : loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status" style={{ color: 'var(--accent-color)' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted text-sm">Preparing payment...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <div className="alert alert-danger text-sm mb-4">
              <strong>Payment Error:</strong> {error}
            </div>
            <button
              type="button"
              className="btn btn-custom-theme-active w-100 py-3 text-white font-semibold"
              onClick={handleReset}
              style={{ borderRadius: '12px', fontSize: '1rem' }}
            >
              Go Back
            </button>
          </div>
        ) : !clientSecret ? (
          <div className="amount-selection-container">
            <p className="text-muted text-sm text-center mb-4">
              Purchase KAX with your card via Stripe. Enter your card details and KAX is sent to your wallet.
            </p>

            <StripeAmountSelector
              usdAmount={usdAmount}
              kaxAmount={kaxAmount}
              onUsdChange={handleUsdChange}
              onKaxChange={handleKaxChange}
              presetAmounts={presetAmounts}
              onPresetClick={handlePresetClick}
              minAmount={10}
              quote={quote}
              quoteLoading={quoteLoading}
              quoteError={quoteError}
            />

            <div className="card bg-light dark:bg-gray-800/10 p-3 mb-4 border-0" style={{ borderRadius: '12px' }}>
              <div className="d-flex align-items-center justify-content-between text-xs text-muted">
                <span>Target Wallet:</span>
                <span className="font-semibold text-dark dark:text-light text-truncate ms-2" style={{ maxWidth: '200px' }}>
                  {walletAddress}
                </span>
              </div>
            </div>

            <button
              type="button"
              className="btn btn-custom-theme-active w-100 py-3 text-white font-semibold"
              onClick={handleProceed}
              disabled={!usdAmount || parseFloat(usdAmount) < 10 || quoteLoading || !quote}
              style={{ borderRadius: '12px', fontSize: '1rem', letterSpacing: '0.5px' }}
            >
              Continue
            </button>
          </div>
        ) : (
          <div id="tour-stripe-payment-form">
            <StripePaymentForm
              clientSecret={clientSecret}
              amount={usdAmount}
              theme={theme}
              onSuccess={() => setPaymentSucceeded(true)}
            />
            {!paymentSucceeded ? (
              <button
                type="button"
                className="btn btn-custom-theme-outline w-100 py-3 mt-3 font-semibold"
                onClick={handleReset}
                style={{ borderRadius: '12px', fontSize: '1rem', letterSpacing: '0.5px' }}
              >
                Cancel
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-custom-theme-outline w-100 py-3 mt-3 font-semibold"
                onClick={handleReset}
                style={{ borderRadius: '12px', fontSize: '1rem', letterSpacing: '0.5px' }}
              >
                Buy More
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StripeOnRamp;
